'use client'

import { useMemo, useState } from 'react'

import { Anchor } from '@/components/client/seo/anchor'
import type { Locale } from '@/i18n'
import { forumProfileUrl } from '@/lib/forum/profile-url'
import type { ReviewWithAvatar } from '@/lib/supabase/get-release-reviews'
import { tw } from '@/lib/utils'
import { formatMonthYearDate } from '@/lib/utils/date-utils'

import { AnonymousAvatar } from './anonymous-avatar'
import { StarRatingDisplay } from './star-rating'

// Character thresholds matching old app. The line thresholds mirror the
// `line-clamp-4` / `lg:line-clamp-6` classes below: a short review with many
// line breaks needs the clamp just as much as a long single paragraph.
const MOBILE_CLAMP_THRESHOLD = 360
const DESKTOP_CLAMP_THRESHOLD = 480
const MOBILE_CLAMP_LINES = 4
const DESKTOP_CLAMP_LINES = 6

// Forum-synced reviews arrive with CRLF line endings; site-form reviews use LF.
const normalizeLineBreaks = (text: string) => text.replace(/\r\n?/g, '\n')

interface ReviewCardProps {
  review: ReviewWithAvatar
  locale: Locale
  anonymousLabel: string
  showMoreLabel: string
  showLessLabel: string
  /** Accessible text for the stars, e.g. "3 out of 5 stars". */
  starsLabel: string
  /** Which model the reviewer bought, e.g. "Model: RIE 63". Passed only
   *  when the family has more than one model. */
  modelNote?: string | null
  className?: string
}

export const ReviewCard = ({
  review,
  locale,
  anonymousLabel,
  showMoreLabel,
  showLessLabel,
  starsLabel,
  modelNote,
  className,
}: ReviewCardProps) => {
  const comments = useMemo(() => normalizeLineBreaks(review.comments), [review.comments])
  const lineCount = useMemo(() => comments.split('\n').length, [comments])
  const shouldClamp = comments.length > MOBILE_CLAMP_THRESHOLD || lineCount > MOBILE_CLAMP_LINES
  const shouldClampDesktop = comments.length > DESKTOP_CLAMP_THRESHOLD || lineCount > DESKTOP_CLAMP_LINES
  const [expanded, setExpanded] = useState(false)

  const displayName = review.userName || anonymousLabel
  // Forum-synced reviews carry the member's forum handle, so the name links
  // back to their profile. Site-form reviews have no profile to point at.
  const profileUrl = review.source === 'forum' ? forumProfileUrl(review.userName) : null

  return (
    <article className={className}>
      <div className="flex items-start gap-4">
        {review.avatarUrl ? (
          <img
            src={review.avatarUrl}
            alt=""
            className="h-[44px] w-[44px] shrink-0 rounded-full bg-white object-contain"
          />
        ) : (
          <AnonymousAvatar size={44} className="shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-navy text-[16px] font-medium">
            {profileUrl ? (
              <Anchor
                href={profileUrl}
                target="_blank"
                rel="nofollow"
                data-tracking="ignore"
                className="text-lapis cursor-pointer hover:underline"
              >
                {displayName}
              </Anchor>
            ) : (
              displayName
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <StarRatingDisplay score={review.score} size={16} label={starsLabel} />
            <span className="text-navy-60 text-[14px]">{formatMonthYearDate(review.createdAt, locale)}</span>
            {modelNote && <span className="text-navy-60 text-[14px]">{modelNote}</span>}
          </div>
        </div>
      </div>
      <div
        className={tw(
          'text-navy/85 mt-4 max-w-[70ch] text-[18px] leading-[1.6] whitespace-pre-line',
          !expanded && shouldClamp && 'line-clamp-4 lg:line-clamp-6',
          !expanded && !shouldClamp && shouldClampDesktop && 'lg:line-clamp-6'
        )}
      >
        {comments}
      </div>
      {(shouldClamp || shouldClampDesktop) && (
        <button
          type="button"
          data-tracking="ignore"
          aria-expanded={expanded}
          data-ht-disclosure-trigger="read-more"
          className="text-lapis mt-2 cursor-pointer text-[16px] font-medium underline decoration-dotted underline-offset-4 hover:decoration-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lapis"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? showLessLabel : showMoreLabel}
        </button>
      )}
    </article>
  )
}
